import { Info, Loader2 } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ScopePicker } from '@/modules/backups/ScopePicker';
import { dialogSurface, flatPrimary } from '@/components/surface/tones';
import type { BackupDestination, BackupSchedule, BackupScope, BackupSource } from '@/types/api';

/**
 * Sets the automatic backup for one source.
 *
 * ## One schedule per source, and saving is an update when one exists
 *
 * The API upserts: choosing a source that already has a schedule changes that schedule rather
 * than adding a second. It changes the destination, the frequency and the copies kept — but not what
 * is backed up, which is fixed when a schedule is created. So for an existing schedule the picker
 * shows the scope it has and says how to change it, instead of offering a choice the save would
 * quietly ignore.
 *
 * ## The error is shown here, not behind the dialog
 *
 * A refusal written to the page would sit underneath the modal still covering it, and read as a
 * button that does nothing.
 */
export function ScheduleDialog({
  open,
  onOpenChange,
  sources,
  destinations,
  schedules,
  sourceId,
  destinationId,
  scope,
  frequency,
  retention,
  onSourceChange,
  onDestinationChange,
  onScopeChange,
  onFrequencyChange,
  onRetentionChange,
  pickerHint,
  error,
  pending,
  canSubmit,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: BackupSource[];
  destinations: BackupDestination[];
  schedules: BackupSchedule[];
  sourceId: string;
  destinationId: string;
  scope: BackupScope;
  frequency: 'daily' | 'weekly';
  retention: number;
  onSourceChange: (id: string) => void;
  onDestinationChange: (id: string) => void;
  onScopeChange: (scope: BackupScope) => void;
  onFrequencyChange: (frequency: 'daily' | 'weekly') => void;
  onRetentionChange: (count: number) => void;
  pickerHint: string | null;
  error: string | null;
  pending: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
}) {
  const existing = schedules.find((schedule) => schedule.source_id === sourceId);
  const source = sources.find((item) => item.id === sourceId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-lg ${dialogSurface}`}>
        <DialogHeader>
          <DialogTitle>Automatic backups</DialogTitle>
          <DialogDescription>Old copies are removed once a newer one exists.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error !== null && <Alert variant="danger" className="border-0">{error}</Alert>}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="schedule-source">Source</Label>
              <Select id="schedule-source" value={sourceId} onChange={(event) => onSourceChange(event.target.value)}>
                <option value="">{sources.length === 0 ? 'No backup sources' : 'Choose…'}</option>
                {sources.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="schedule-dest">Destination</Label>
              <Select id="schedule-dest" value={destinationId} onChange={(event) => onDestinationChange(event.target.value)}>
                <option value="">Choose…</option>
                {destinations.map((destination) => (
                  <option key={destination.id} value={destination.id}>
                    {destination.name}
                    {destination.is_offsite ? '' : ' (on the server)'}
                  </option>
                ))}
              </Select>
              {destinations.length === 0 && (
                <p className="text-xs text-muted-foreground">No destinations yet. Add one first.</p>
              )}
            </div>
          </div>

          {pickerHint !== null && <p className="-mt-2 text-xs text-muted-foreground">{pickerHint}</p>}

          {existing !== undefined && (
            <p className="flex items-start gap-2 rounded-lg bg-primary/[0.07] px-3 py-2 text-xs text-muted-foreground">
              <Info className="mt-px size-3.5 shrink-0 text-primary" aria-hidden />
              <span>
                {existing.source_name} already has a schedule. Saving changes its destination, how
                often it runs and how many copies it keeps.
              </span>
            </p>
          )}

          <ScopePicker
            id="schedule-scope"
            value={existing?.scope ?? scope}
            onChange={onScopeChange}
            source={source}
            disabled={existing !== undefined}
            note={
              existing !== undefined
                ? 'Fixed when the schedule was created. To change it, remove the schedule and add it again.'
                : undefined
            }
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="schedule-freq">How often</Label>
              <Select
                id="schedule-freq"
                value={frequency}
                onChange={(event) => onFrequencyChange(event.target.value as 'daily' | 'weekly')}
              >
                <option value="daily">Every day</option>
                <option value="weekly">Every week</option>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="schedule-keep">Keep</Label>
              <div className="relative">
                <Input
                  id="schedule-keep"
                  type="number"
                  min={1}
                  max={30}
                  value={retention}
                  onChange={(event) => onRetentionChange(Number(event.target.value))}
                  className="pr-16"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  copies
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit} className={flatPrimary}>
            {pending && <Loader2 className="animate-spin" />}
            Save schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
